import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";
import { stripMarkdown } from "@/lib/markdown";

/**
 * Safe Markdown renderer for AI-generated text.
 *
 * - No raw HTML/scripts (skipHtml).
 * - Links open in a new tab with rel="noopener noreferrer".
 * - Supports bold, italic, headings, ordered/unordered lists, tables, code
 *   blocks, blockquotes, and preserves line breaks.
 * - Copy-to-clipboard from anywhere in the document strips markdown symbols so
 *   copied text never contains raw ** or #.
 * - `citationMap` (optional): map of citation number -> URL. When present, any
 *   [1]/[2] style citations render as clickable superscript badges to that URL
 *   instead of literal brackets.
 */
export default function AIMarkdown({
  text,
  className,
  citationMap,
  testId,
  size = "sm",
}) {
  const source = String(text || "");
  const nodes = useMemo(
    () => interpolateCitations(source, citationMap),
    [source, citationMap],
  );
  const cls = cn(
    "ai-markdown prose max-w-none",
    "prose-p:my-1.5 prose-p:leading-relaxed",
    "prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5",
    "prose-headings:font-semibold prose-headings:my-2",
    "prose-h1:text-base prose-h2:text-sm prose-h3:text-sm prose-h4:text-sm",
    "prose-strong:font-semibold",
    "prose-a:text-[#255058] prose-a:underline hover:prose-a:text-[#1c3d43]",
    "prose-code:bg-black/5 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[0.85em]",
    "prose-pre:bg-black/5 prose-pre:p-3 prose-pre:rounded-md prose-pre:overflow-x-auto",
    "prose-table:my-2 prose-th:font-semibold prose-th:text-left prose-th:px-2 prose-th:py-1",
    "prose-td:px-2 prose-td:py-1 prose-th:border prose-td:border",
    size === "sm" ? "text-sm" : size === "xs" ? "text-xs" : "text-base",
    className,
  );
  return (
    <div className={cls} data-testid={testId || "ai-markdown"}>
      <ReactMarkdown
        skipHtml
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children, ...rest }) => (
            <a
              href={href || "#"}
              target="_blank"
              rel="noopener noreferrer"
              {...rest}
            >
              {children}
            </a>
          ),
        }}
      >
        {nodes}
      </ReactMarkdown>
    </div>
  );
}

// When a citationMap is provided, keep [n] tokens intact so react-markdown sees
// them; then post-process with a walker in the wrapper below. For simplicity we
// convert citations directly to markdown links to the source URL — react-
// markdown then renders them as small inline links (also stripped on copy).
function interpolateCitations(text, citationMap) {
  if (!citationMap || !text) return text;
  return text.replace(/\[(\d+)\]/g, (m, num) => {
    const url = citationMap[num];
    if (!url) return m;
    return ` [[${num}]](${url})`;
  });
}

export { stripMarkdown };
